/**
 * Beat 10 stage 4: twelve finishing pigs, one pipe, and a clock that started
 * when the first of them moved.
 *
 * The sibling of `twelveOut.ts` and `nightDog.ts`, and deliberately the same
 * shape: fixed-step facts and nothing else. No Three, no Rapier, no room
 * coordinates — `src/story/` does not import `src/world/`, so the route arrives
 * as a scalar length through `CulvertQueueOptions` and the room is what states
 * it, projects Rosalie onto it, and draws twelve bodies at the distances this
 * object reports. `tests/e2e/culvert-queue.spec.ts` holds the two ends together
 * and runs in Node, because nothing here needs a frame to be true.
 *
 * ── Why this is a module and not the director's private state ───────────────
 *
 * Act 3 inherits it for forty pigs on a loading ramp (`docs/act-3-plan.md`).
 * A queue that lived inside `HedgerowDirector` would be re-derived there from a
 * reading of this beat's behaviour rather than from this beat's arithmetic, and
 * the two would disagree in the third place after the decimal point, which is
 * exactly the size of disagreement that changes a survivor count by one.
 *
 * ── Why nothing in here is authored about how many get out ──────────────────
 *
 * `docs/story-review.md` S-7, in its own words: an authored survivor count is a
 * lie the player finds on a second run, in a game whose thesis is that the world
 * does not cheat, it just gets harder. So the count is what falls out of route
 * length, herd speed, service time and the schedule — four numbers the player
 * can watch — and **the spec asserts that neither measured count appears as a
 * literal anywhere in this file**. That assertion is the whole of acceptance
 * gate 6 and it is the reason to resist writing `if (perfect) return 10`.
 */

/** Twelve is the beat's authored herd. Fewer removes the queue's pressure; more makes the terminal arrive before the tail has left the huddle. */
export const HERD_SIZE = 12;

/**
 * Seconds one animal takes to get through the pipe.
 *
 * This is the whole throughput budget. Twelve animals is 48 s of pure service
 * against a terminal at 52 s — but **twelve is never on the table**: the first
 * animal cannot reach the mouth before `routeLength / HERD_SPEED`, which on the
 * hedgerow's 16.978 m route is 10.95 s, so the ceiling is ten. That is the
 * intended answer (`docs/beat-10-plan.md`: *perfect play gets nine or ten*) and
 * it is stated here because the 48-against-52 framing reads like four seconds of
 * slack that does not exist. Raise it and perfect play stops clearing ten; lower it and the
 * clock stops mattering, because everyone is through before the dogs are near.
 */
export const SERVICE_SECONDS = 4.0;

/**
 * Metres per second along the route. Below Rosalie's 1.9 m/s trot on purpose.
 *
 * A herd that matched her trot would make leading them free — she would never
 * have to look back, and `DRAW_RANGE` would never bind. Below it, holding the
 * string means moderating her own pace, which is the verb of this stage. Much
 * further below and the walk, rather than the pipe, becomes the bottleneck and
 * the 4 s service stops being felt at all.
 */
export const HERD_SPEED = 1.55;

/**
 * Metres between members of the string once it is strung out.
 *
 * It sets the arrival cadence at the pipe mouth — one animal every
 * `HERD_SPACING / HERD_SPEED` seconds — and that cadence must stay comfortably
 * under `SERVICE_SECONDS`, or the queue never backs up and the pipe stops being
 * the constraint the beat is about. Wider also means a longer string than
 * `DRAW_RANGE`, which would make the tail unleadable from the front.
 */
export const HERD_SPACING = 1.10;

/**
 * How far ahead the leader may get before the string stops following, metres.
 *
 * Ten metres against a strung-out string of `(HERD_SIZE - 1) * HERD_SPACING`
 * = 12.1 m is deliberate: she cannot walk to the pipe and wait there while they
 * come, because before the front animal arrives the head of the string is the
 * front animal and the gap opens past ten. Raising it removes the shepherding;
 * lowering it makes her lead from inside the herd, where the camera has nothing
 * to show.
 *
 * ── What it does NOT do, because the paragraph above used to imply it ────────
 *
 * **It binds only before the first arrival.** `#recomputeHead` takes the head
 * off the front of what is still *walking*, so it steps back one `HERD_SPACING`
 * at every arrival — deliberately, and it is what keeps the herd coming while
 * she stands at the mouth. Once animals are arriving she cannot be ten metres
 * ahead of the head however she plays, which is why the review measured
 * **trotting straight to the mouth and standing there scoring identically to
 * shepherding: 10 out either way.** This constant prevents *starting* at the
 * mouth and prevents opening a gap on the walk down. It does not do more, and
 * `culvert-queue.spec.ts`'s corridor arm is the gate that would notice if
 * somebody made it try.
 *
 * ── Why it is not raised, although rolling the fall strands the herd ─────────
 *
 * D-174, and it is arithmetic rather than taste. At `rollMaxSpeed` 4.5 against
 * `HERD_SPEED` 1.55 the gap passes ten after 3.4 s of rolling and the string
 * stops where it stands — measured, **0 out**. The obvious repair is to let the
 * string trot to catch up, and the beat's own ceiling forbids it: the first
 * animal cannot reach the mouth before `routeLength / HERD_SPEED` = 10.95 s,
 * which with a 4.0 s service against a 52 s terminal is what caps the herd at
 * ten. Any speed fast enough to close a rolled gap also brings that arrival
 * forward far enough for all twelve to clear the terminal, and spec §7.10 says
 * *"You will not get all twelve out"* in as many words.
 *
 * So **the roll is a spend, the same shape as the bell**: it buys her seconds
 * and it costs the herd theirs. What stage 5 owes is not a bigger number here,
 * it is that the stall has no voice — see `docs/beat-10-plan.md`, stage C.
 */
export const DRAW_RANGE = 10.0;

/**
 * How far off the route line the leader may be and still be led, metres.
 *
 * Six metres is a lane, not a line: the player is dodging a dog while doing
 * this and a corridor she can be knocked out of by one roll would read as the
 * herd punishing her for the dog. Wider and there is no route at all, only a
 * general direction; narrower and the stage becomes a walk down a rail.
 */
export const LEAD_CORRIDOR = 6.0;

/**
 * Seconds a refusal must hold continuously before the string counts as stalled.
 *
 * The stall is the only thing in this beat that speaks to the player, so it has
 * to be right in both directions. Shorter and one sidestep around the dog —
 * which is the other thing she is doing on this slope, and D-167's whole point —
 * reports a stall she has already fixed by the time she hears about it. Longer
 * and the cause has left the screen before the consequence arrives: at
 * `rollMaxSpeed` 4.5 she covers four and a half metres a second, so a second of
 * delay is a call that arrives from somewhere she was not when she did the thing
 * it is about.
 *
 * It is not a debounce on noisy input. `lead` comes from `culvertRoutePlace` on
 * her actual position and does not chatter; this is a grace period on a decision
 * about the player, and 0.6 s is the length of a mistake she is still making.
 */
export const STALL_GRACE = 0.6;

/**
 * Metres the string must actually cover before the clock is running, and this
 * constant exists because the beat lost its opening without it.
 *
 * "A clock starts when the first animal moves" is the plan's wording and it was
 * implemented literally: any advance at all, however small, started the 52 s
 * schedule. **In the room that is not a departure, it is a shuffle.** Rosalie
 * arriving at the huddle settles onto the ground and shifts a few centimetres,
 * her projected distance ticks above the head's by 0.02 m, the string closes
 * that gap, and the clock is running — which silently deletes the one property
 * D-169 claims for the opening: *the player may stand in the moonlight counting
 * the huddle for as long as they like without spending a second.*
 *
 * Found by `beat-10-herd.spec.ts`'s control arm, where she was put at the huddle
 * and told to do nothing, and 52 s later every animal had scattered.
 *
 * Half a metre is longer than any settle and shorter than a step. Raise it and
 * the herd is visibly walking before the clock starts, which is a lie in the
 * other direction; lower it and the jitter comes back.
 */
export const MUSTER_DISTANCE = 0.5;

export type MemberState = 'huddled' | 'moving' | 'queued' | 'passing' | 'out' | 'scattered';

/**
 * Seconds from the first movement. The last entry is the terminal.
 *
 * Authored so the player can hear it coming (`docs/beat-10-plan.md`, stage D):
 * three warnings and then the thing itself. The gaps shorten — 18, 16, 12, 6 —
 * so the pressure reads as closing rather than as a metronome.
 *
 * **Ascending order is load-bearing.** `#fireSchedule` walks a cursor rather
 * than scanning, so an entry out of order would never fire and the terminal
 * would never arrive; the constructor asserts the order rather than trusting
 * this comment, because a comment that claims something cannot happen must name
 * the gate that enforces it.
 */
export const SCHEDULE = [
  { id: 'far', at: 18.0 },
  { id: 'near', at: 34.0 },
  { id: 'closing', at: 46.0 },
  { id: 'arrive', at: 52.0 },
] as const;

export interface CulvertQueueOptions {
  /** Length of the room's authored route, huddle to pipe mouth, in metres. */
  readonly routeLength: number;
}

/** Where the leader is, expressed on the route. The ROOM does the projection. */
export interface CulvertQueueLead {
  /** Her distance along the route, metres, already clamped to [0, routeLength]. */
  readonly distance: number;
  /** Her perpendicular distance from the route line, metres. */
  readonly offset: number;
}

export interface CulvertQueueMember {
  readonly index: number;
  readonly state: MemberState;
  /** Metres along the route. */
  readonly distance: number;
}

export interface CulvertQueueSnapshot {
  readonly members: readonly CulvertQueueMember[];
  /**
   * The head of the string, metres along the route.
   *
   * **This is the foremost animal still walking, not a high-water mark**, so it
   * steps BACK by one `HERD_SPACING` each time an animal reaches the pipe and
   * leaves the string. That is not a defect and it is what keeps the rest of
   * the herd coming while Rosalie stands at the mouth: see `#recomputeHead`.
   */
  readonly headDistance: number;
  /** Seconds since the first movement, or null before it. */
  readonly clock: number | null;
  /** True on any step where the string covered ground. */
  readonly drawn: boolean;
  /** True while the string has been refusing to follow for STALL_GRACE. */
  readonly stalled: boolean;
  /** Why, when `stalled`. Null otherwise. `'behind'` is never a value here. */
  readonly stallReason: 'range' | 'offset' | 'blocked' | null;
  /**
   * How many stalls have latched. Monotonic; this is the instrument.
   *
   * A stall is a transient phase once Rosalie returns, so a gate that samples
   * it is a race that happens to win. This counter is the latched fact behind
   * the voice, in the same form as `NightDogSnapshot.drives`.
   */
  readonly stalls: number;
  readonly out: number;
  readonly scattered: number;
  readonly queued: number;
  /** Which member is in the pipe, or null. There is exactly one slot. */
  readonly serving: number | null;
  readonly serviceRemaining: number;
  readonly ended: boolean;
  /**
   * How many animals have gone through. Monotonic; this is the instrument.
   *
   * Copied in form from `NightDogSnapshot.drives` and for its reason: `passing`
   * is a 4 s transient and `queued` empties, so a gate that samples a state is a
   * race that happens to win. This counter is the latched fact behind the whole
   * beat, and it is the number the room's HUD and act 3's ramp both read.
   */
  readonly throughs: number;
  /** The schedule entry most recently fired, or null. */
  readonly lastCue: string | null;
  /**
   * How many schedule entries have fired. Exposed so `reset()` is CHECKABLE.
   *
   * It was private, and `reset()`'s own gate is a deep-equal against the
   * construction snapshot — so removing `this.#cursor = 0` from `reset()` left
   * that assertion green while the replayed beat **never ended at all**: with
   * the cursor past `SCHEDULE`, `#fireSchedule`'s `while` body never runs and
   * `#terminate` is never reached. An independent review broke it and watched
   * the wrong test catch it. A field a gate cannot see is a field that gate does
   * not cover.
   */
  readonly cuesFired: number;
}

export type CulvertQueueEvent =
  | { readonly kind: 'muster' }
  | {
      readonly kind: 'stall';
      readonly reason: 'range' | 'offset' | 'blocked';
      /**
       * The clock the stall latched at, and it is on the event because the
       * consumer cannot recover it.
       *
       * `main.ts` schedules three calls at fixed offsets from the latch, and it
       * drains this queue on the RENDER step. A render frame can be a whole
       * simulated second behind the step that pushed the event — that is exactly
       * what `fastForward` produces, one frame per simulated second — so a
       * consumer that used the clock it happened to read would slide the whole
       * sequence, and a long enough hitch would fire all three in one frame.
       * Found by an independent review; the comments claiming "simulation
       * seconds from the latch" were true of the intent and not of the code.
       */
      readonly at: number;
    }
  | { readonly kind: 'regain' }
  | { readonly kind: 'through'; readonly index: number }
  | { readonly kind: 'cue'; readonly id: string }
  | { readonly kind: 'end' };

/** Returned by every inert step, so the dogs having arrived costs no allocation. */
const NO_EVENTS: readonly CulvertQueueEvent[] = [];

type DrawRefusal = 'offset' | 'behind' | 'range' | 'blocked';

export class CulvertQueue {
  readonly #options: CulvertQueueOptions;

  /**
   * Ground the STRING has covered, which is not the same as the head's distance.
   *
   * The herd is one scalar plus a fixed offset per member — member `i` sits at
   * `travelled - i * HERD_SPACING`, clamped into the route — because they are a
   * string of animals following a leader and not twelve pathing agents (S-7's
   * own words, and the cheaper design as well as the honest one).
   *
   * **It is `#travelled` and not `headDistance` that carries the string past the
   * pipe.** The last animal is `(HERD_SIZE - 1) * HERD_SPACING` behind the
   * first, so every member reaching the mouth requires the string to have
   * covered `routeLength` plus that, while no *animal* ever stands past the
   * mouth. Conflating the two caps the whole herd at one survivor, which is the
   * arithmetic this field exists to keep straight.
   */
  #travelled = 0;

  /** The foremost member still walking. Recomputed once per step; see `#recomputeHead`. */
  #headDistance = 0;

  readonly #states: MemberState[] = [];

  /**
   * Arrival order, FIFO, and it is a real queue rather than an index scan.
   *
   * For a single string arrival order *is* index order and a scan would agree
   * with this on every trace this beat can produce. Act 3's ramp is not a single
   * string — forty animals arrive from two sides — and a scan silently becomes
   * "lowest index first" there, which is a fairness rule nobody chose. The
   * structure is what carries the intent across the inheritance.
   */
  readonly #queue: number[] = [];

  #serving: number | null = null;
  #serviceRemaining = 0;
  #clock: number | null = null;
  #cursor = 0;
  #lastCue: string | null = null;
  #throughs = 0;
  #ended = false;
  #drawn = false;
  #stalled = false;
  #stallReason: 'range' | 'offset' | 'blocked' | null = null;
  #stallCandidate: 'range' | 'offset' | 'blocked' | null = null;
  #stallElapsed = 0;
  #stalls = 0;

  constructor(options: CulvertQueueOptions) {
    if (!Number.isFinite(options.routeLength) || options.routeLength <= 0) {
      // A zero-length route puts every member at the mouth on the first drawn
      // step, which is a queue with no walk in front of it: the stage's whole
      // trade — seconds spent leading against seconds left on the clock —
      // silently evaluates to nothing and the spec's two traces would agree.
      throw new Error('culvert queue needs a finite positive route length');
    }
    for (let entry = 1; entry < SCHEDULE.length; entry += 1) {
      const previous = SCHEDULE[entry - 1];
      const current = SCHEDULE[entry];
      if (previous === undefined || current === undefined || current.at <= previous.at) {
        // This is the gate the `SCHEDULE` comment names. `#fireSchedule` walks a
        // cursor, so an entry authored out of order is not a late cue, it is a
        // cue that never fires — and if it were the last one, a stage with no
        // terminal at all.
        throw new Error('culvert queue schedule must be strictly ascending');
      }
    }
    this.#options = options;
    for (let index = 0; index < HERD_SIZE; index += 1) this.#states.push('huddled');
  }

  /**
   * Where member `i` stands, derived rather than stored.
   *
   * Deriving is what makes two of the spec's invariants structural instead of
   * assertions that happen to hold: the clamp is monotone non-increasing in `i`
   * (nobody overtakes the animal in front) and `#travelled` never decreases
   * (nobody walks back up the field). A per-member position would make both of
   * those a thing the code has to remember to do on every branch.
   *
   * A member that arrived reads `routeLength` for free, because arriving means
   * `#travelled - i * HERD_SPACING` passed it and the clamp holds it there. A
   * member scattered mid-field freezes where it stood, because the module goes
   * inert and `#travelled` stops.
   */
  #distanceOf(index: number): number {
    const raw = this.#travelled - index * HERD_SPACING;
    if (raw <= 0) return 0;
    return raw >= this.#options.routeLength ? this.#options.routeLength : raw;
  }

  /**
   * The head is the front of what is still WALKING, and that is the whole trick.
   *
   * The obvious reading — head is the string's high-water mark — makes the beat
   * impossible: `lead.distance` is clamped to the route, so once the front
   * animal is at the mouth the leader can never be "ahead of the head" again,
   * `#draw` refuses on every subsequent step, and eleven animals stand in the
   * field until the dogs come. Taking the head off the front of the *remaining*
   * string instead means Rosalie at the mouth is one spacing ahead of whoever is
   * next, the string keeps closing up, and the arrival cadence is
   * `HERD_SPACING / HERD_SPEED` — which is what the stage's arithmetic assumes.
   *
   * The cost, stated plainly because it surprises: `headDistance` is NOT
   * monotonic. It drops by a spacing at each arrival. Member distances are
   * monotonic; the head of a shortening string is not, and the spec asserts the
   * first while deliberately not asserting the second.
   *
   * With nobody left walking there is no front, and the mouth is the honest
   * answer — every remaining member is standing at it.
   */
  #recomputeHead(): void {
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const state = this.#states[index];
      if (state === 'huddled' || state === 'moving') {
        this.#headDistance = this.#distanceOf(index);
        return;
      }
    }
    this.#headDistance = this.#options.routeLength;
  }

  /**
   * Advance the string, if she is leading it. Three separate refusals.
   *
   * Each of them is a different sentence about the fiction — she is off in the
   * field, she is behind them, she has left them behind — and the spec tests
   * them one at a time for that reason: a combined case tells you the string did
   * not move and not which rule stopped it.
   *
   * The advance is clamped to the gap so the head cannot overrun her within a
   * step. That clamp is why `headDistance <= lead.distance` holds on any trace
   * whose leader does not retreat, and it is stated that way — a leader who
   * walks back up the field is behind a head that stays where it is, which is
   * correct behaviour and not an invariant violation.
   */
  #draw(dt: number, lead: CulvertQueueLead, barrier: number | null, events: CulvertQueueEvent[]): DrawRefusal | null {
    if (lead.offset > LEAD_CORRIDOR) return 'offset';
    const gap = lead.distance - this.#headDistance;
    // **Kept although `advance <= 0` below subsumes it**, and a review was right
    // to point out that no test can tell the two apart. It stays because the
    // three refusals are three sentences about the fiction — off in the field,
    // behind them, gone on without them — and collapsing one of them into an
    // arithmetic side effect is how the next reader loses the middle one.
    if (gap <= 0) return 'behind';
    if (gap > DRAW_RANGE) return 'range';
    // The fourth refusal, and it is a CLAMP rather than a test: the dog does not
    // stop the string where it stands, it stops it where the crossing is.
    const barrierGap = barrier === null ? Number.POSITIVE_INFINITY : Math.max(0, barrier - this.#headDistance);
    const advance = Math.min(HERD_SPEED * dt, gap, barrierGap);
    // **`barrierGap` and not `barrier !== null` decides the reason.** A step
    // with `dt` 0 is legal — the guard in `step` admits it — and with a barrier
    // set it advances nothing for a reason that has nothing to do with the dog.
    // Blaming that on the animal puts a `'blocked'` candidate into the stall
    // latch on a tick where the string was never refused anything.
    if (advance <= 0) return barrierGap <= 0 ? 'blocked' : null;
    this.#travelled += advance;
    this.#drawn = true;
    if (this.#clock === null && this.#travelled >= MUSTER_DISTANCE) {
      // The clock starts when the herd has actually left, not on the first
      // centimetre — see `MUSTER_DISTANCE`, which records the shuffle that used
      // to start it. The player may stand in the moonlight counting the huddle
      // for as long as they like without spending a second of the schedule,
      // which is the difference between a stage with a timer in it and a stage
      // that is about a decision to start.
      this.#clock = 0;
      events.push({ kind: 'muster' });
    }
    return null;
  }

  #hasFollowingMember(): boolean {
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const state = this.#states[index];
      if (state === 'huddled' || state === 'moving') return true;
    }
    return false;
  }

  /** Turn a continuous refusal into the player-facing, latched fact. */
  #evaluateStall(dt: number, refusal: DrawRefusal | null, events: CulvertQueueEvent[]): void {
    // **A regain is her being back WITH them, and that is two situations.**
    //
    // The obvious one is that the string covered ground again. The other is
    // `'behind'` — she has walked back up the field past the head, so the gap is
    // negative and nothing advances — and it must count, because a stall left
    // latched while she stands in the middle of her own herd is a state that
    // reports her as having abandoned animals she is touching. The first version
    // of this branch tested `#drawn` alone and did exactly that.
    if (this.#stalled && (this.#drawn || refusal === 'behind')) {
      this.#stalled = false;
      this.#stallReason = null;
      this.#stallCandidate = null;
      this.#stallElapsed = 0;
      events.push({ kind: 'regain' });
      return;
    }

    // Before the muster, no string is following and D-169 lets the player count
    // the moonlit huddle for free; after the end, the dogs have settled the
    // stage; and without a huddled or moving member there is no string left to
    // lose. None of those three states may produce a stall or a voice.
    if (this.#clock === null || this.#ended || !this.#hasFollowingMember()) {
      // **And it CLEARS a latched stall rather than leaving it standing.** With
      // nobody left walking there is no string to be stranded, so `stalled`
      // would otherwise stay true against a herd that is entirely queued, out or
      // scattered — an independent review produced exactly that: hold an offset
      // of 7 m to the terminal and the snapshot reads `ended: true,
      // scattered: 12, stalled: true`. No `regain` goes with it: she did not get
      // them back, the stage ended.
      this.#stalled = false;
      this.#stallReason = null;
      this.#stallCandidate = null;
      this.#stallElapsed = 0;
      return;
    }

    // When she waits, the head closes to her heels and this refusal repeats.
    // They are with her, not stranded, so treating `!drawn` as a stall would
    // teach the player the opposite of the rule the actual stalls exist to say.
    if (refusal !== 'range' && refusal !== 'offset' && refusal !== 'blocked') {
      if (!this.#stalled) {
        this.#stallCandidate = null;
        this.#stallElapsed = 0;
      }
      return;
    }
    if (this.#stalled) return;

    if (this.#stallCandidate !== refusal) {
      this.#stallCandidate = refusal;
      this.#stallElapsed = 0;
    }
    this.#stallElapsed += dt;
    if (this.#stallElapsed < STALL_GRACE) return;
    this.#stalled = true;
    this.#stallReason = refusal;
    this.#stalls += 1;
    // `#clock` is non-null here: the guard above returns otherwise. The `?? 0`
    // is the type system's, not a fallback anybody should ever see.
    events.push({ kind: 'stall', reason: refusal, at: this.#clock ?? 0 });
  }

  #collectArrivals(): void {
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const state = this.#states[index];
      if (state !== 'huddled' && state !== 'moving') continue;
      const distance = this.#distanceOf(index);
      if (distance >= this.#options.routeLength) {
        this.#states[index] = 'queued';
        this.#queue.push(index);
      } else if (distance > 0) {
        this.#states[index] = 'moving';
      }
    }
  }

  /**
   * One slot, at most one animal through per step, and the remainder is dropped.
   *
   * **A slot that can drain twice in one step is not a queue.** The game steps
   * at a fixed 60 Hz, but this class is handed whatever `dt` a caller has, and a
   * test — or a resumed tab, or a stage that fast-forwards — offering a ten
   * second slice must not empty the pipe. So the decrement happens once, the
   * completion happens once, and the animal that starts next is deliberately
   * NOT decremented on the step it starts.
   *
   * The overshoot past zero is discarded rather than carried. Carrying it is the
   * more "correct" choice for a variable-`dt` simulation and it is the wrong one
   * here: it would let a single long slice bank credit against the next animal
   * and, over a queue of twelve, hand back most of the time the one-per-step
   * rule just refused. At the beat's real 60 Hz the discard is at most one tick
   * per animal — under 0.2 s across the whole herd, against a 4 s service — and
   * that error is bounded, one-signed, and in the player's disfavour, which is
   * the only direction this beat is allowed to be wrong in.
   */
  #serveOne(dt: number, events: CulvertQueueEvent[]): void {
    const serving = this.#serving;
    if (serving !== null) {
      this.#serviceRemaining -= dt;
      if (this.#serviceRemaining > 0) return;
      this.#states[serving] = 'out';
      this.#serviceRemaining = 0;
      this.#serving = null;
      this.#throughs += 1;
      events.push({ kind: 'through', index: serving });
    }
    const next = this.#queue.shift();
    if (next === undefined) return;
    this.#serving = next;
    this.#states[next] = 'passing';
    this.#serviceRemaining = SERVICE_SECONDS;
  }

  #fireSchedule(events: CulvertQueueEvent[]): void {
    const clock = this.#clock;
    if (clock === null) return;
    while (this.#cursor < SCHEDULE.length) {
      const entry = SCHEDULE[this.#cursor];
      if (entry === undefined || clock < entry.at) return;
      this.#cursor += 1;
      this.#lastCue = entry.id;
      events.push({ kind: 'cue', id: entry.id });
      if (this.#cursor === SCHEDULE.length) {
        this.#terminate(events);
        return;
      }
    }
  }

  /**
   * The dogs arrive. Everyone not already through is scattered, including the
   * animal that is in the pipe right now.
   *
   * **This is the arguable rule and here is the argument, so it does not have to
   * be reconstructed.** Letting a mid-service animal finish reads as merciful
   * and costs the beat its point: it makes the last second of the clock free,
   * and a player who learns that starts every run by spending the four seconds
   * they were given for nothing. Worse, it is a rule the player cannot see —
   * from outside the pipe, an animal halfway through and an animal about to
   * enter look identical, so a game that treats them differently is a game that
   * cheated in the player's favour without telling them, which is the same
   * dishonesty as S-7's authored count wearing the other face.
   *
   * The fiction agrees: the pipe stops being available. It is not a door that
   * closes behind whoever made it in, it is a place that is no longer safe to be
   * standing at.
   *
   * The head is not recomputed here, so `headDistance` freezes at the front of
   * the string as it was when the dogs came, rather than jumping to the mouth
   * because nobody is walking any more.
   */
  #terminate(events: CulvertQueueEvent[]): void {
    for (let index = 0; index < HERD_SIZE; index += 1) {
      if (this.#states[index] !== 'out') this.#states[index] = 'scattered';
    }
    this.#queue.length = 0;
    this.#serving = null;
    this.#serviceRemaining = 0;
    this.#ended = true;
    // The dogs came. Whatever the string was doing, it is not doing it any more,
    // and `stalled` is defined as *"the string is refusing to follow"* — which
    // needs a string. `#stalls` is untouched: it is the latched history and she
    // did lose them.
    this.#stalled = false;
    this.#stallReason = null;
    this.#stallCandidate = null;
    this.#stallElapsed = 0;
    events.push({ kind: 'end' });
  }

  /**
   * One fixed step. Returns what happened during it, in the order it happened.
   *
   * The order inside is load-bearing and it is the same argument `main.ts` makes
   * about `fixedUpdate`: the clock advances first so a step's `dt` is counted
   * exactly once and never against the movement it also paid for; movement then
   * its stall latch, so the voice observes the refusal that actually happened;
   * arrivals then service, so an animal that reaches the mouth on a step can
   * begin its 4 s on that same step rather than idling the pipe for a tick; and
   * the schedule last, so an animal whose service completes on the very step the
   * dogs arrive is `out`. It was through the pipe when they came. That is the
   * one place the boundary falls in the player's favour and it is the honest
   * one — the alternative would scatter an animal that had already gone.
   */
  /**
   * Required so every caller must state whether the dog guards the route; a
   * default would silently omit that fiction at the next call site. This has no
   * leader-past/tail-not state: it is a rigid string, so while the dog is home
   * it freezes the WHOLE string at or past the balk, including an animal halfway
   * down the route, rather than simulating per-leader movement. Deliberately,
   * that makes repeated calls matter instead of one keypress releasing a
   * barrier forever. It cannot deadlock: SCHEDULE's 52 s terminal and TwelveOut
   * are the backstops that end the run.
   */
  step(dt: number, lead: CulvertQueueLead, barrier: number | null): readonly CulvertQueueEvent[] {
    // Same guard, same wording and same reason as `NightDog.step`: a NaN `dt`
    // does not throw anywhere downstream, it silently poisons `#travelled` and
    // then every distance derived from it, and the first visible symptom is
    // twelve animals that have stopped existing several seconds later.
    if (!Number.isFinite(dt) || dt < 0) throw new Error('culvert queue step must use a finite non-negative dt');
    if (this.#ended) return NO_EVENTS;
    const events: CulvertQueueEvent[] = [];
    this.#drawn = false;
    if (this.#clock !== null) this.#clock += dt;
    const refusal = this.#draw(dt, lead, barrier, events);
    this.#evaluateStall(dt, refusal, events);
    this.#collectArrivals();
    this.#recomputeHead();
    this.#serveOne(dt, events);
    this.#fireSchedule(events);
    return events;
  }

  /**
   * Allocates; `step` does not. The sibling modules draw the line in the same
   * place, and this one is called once per render frame by the room while the
   * step runs at 60 Hz whatever the display does.
   */
  snapshot(): CulvertQueueSnapshot {
    const members: CulvertQueueMember[] = [];
    let out = 0;
    let scattered = 0;
    let queued = 0;
    for (let index = 0; index < HERD_SIZE; index += 1) {
      const state = this.#states[index] ?? 'huddled';
      if (state === 'out') out += 1;
      else if (state === 'scattered') scattered += 1;
      else if (state === 'queued') queued += 1;
      members.push({ index, state, distance: this.#distanceOf(index) });
    }
    return {
      members,
      headDistance: this.#headDistance,
      clock: this.#clock,
      drawn: this.#drawn,
      stalled: this.#stalled,
      stallReason: this.#stallReason,
      stalls: this.#stalls,
      out,
      scattered,
      queued,
      serving: this.#serving,
      serviceRemaining: this.#serviceRemaining,
      ended: this.#ended,
      throughs: this.#throughs,
      lastCue: this.#lastCue,
      cuesFired: this.#cursor,
    };
  }

  /**
   * Every field back to construction, in one statement, and that is the point.
   *
   * `TwelveOut.reset()`'s header is the standing warning here: a reset that
   * restores one half of a pair surfaces one tick later as an exception nobody
   * can place. This object has four such pairs — `#travelled` with
   * `#headDistance`, `#states` with `#queue`, `#serving` with
   * `#serviceRemaining`, `#clock` with `#cursor` and `#lastCue` — and each of
   * them fails in a different, quiet way if only one side is cleared: a queue
   * holding indices of animals that are huddled again would serve an animal
   * standing in the field, and **a cursor left past `SCHEDULE` means the stage
   * never ends at all** — `#fireSchedule` is a `while (cursor < SCHEDULE.length)`
   * and with the cursor at 4 the body never runs, so `#terminate` is never
   * reached and every one of the twelve gets through. (The first version of this
   * comment said such a cursor would end the stage on its first step, which is
   * the exact opposite; a review broke the field and measured `out: 12`.)
   *
   * The spec's gate is `reset()` producing a snapshot deep-equal to the
   * construction snapshot. **That only covers what the snapshot exposes**, which
   * is why `cuesFired` is on it — the cursor was private, and the break above
   * was caught by the replay assertion two lines further down rather than by the
   * one whose comment claimed to make "completely" checkable.
   */
  reset(): void {
    this.#travelled = 0;
    this.#headDistance = 0;
    for (let index = 0; index < HERD_SIZE; index += 1) this.#states[index] = 'huddled';
    this.#queue.length = 0;
    this.#serving = null;
    this.#serviceRemaining = 0;
    this.#clock = null;
    this.#cursor = 0;
    this.#lastCue = null;
    this.#throughs = 0;
    this.#ended = false;
    this.#drawn = false;
    this.#stalled = false;
    this.#stallReason = null;
    // **These two are belt-and-braces and NO GATE CAN SEE THEM, which is worth
    // saying rather than leaving as a test somebody will try to write.** An
    // independent review pointed out that the reset arm above runs `goodLead`
    // first, so both are already at their defaults when it fires — true, and the
    // deeper reason is that `#evaluateStall`'s first branch clears them on any
    // step where the clock is null, which is every step after a reset until the
    // string musters again. So removing these lines changes no reachable
    // behaviour. They stay because the alternative is a `reset()` that restores
    // some of its object, and this class's own header is about what that costs.
    this.#stallCandidate = null;
    this.#stallElapsed = 0;
    this.#stalls = 0;
  }
}
